// Billing Types and Interfaces

export enum SubscriptionTier {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
  INDUSTRY_SPECIFIC = 'industry_specific',
  CUSTOM = 'custom'
}

export enum BillingFrequency {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}

export enum SubscriptionStatus {
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  SUSPENDED = 'suspended'
}

export interface PricingTier {
  id: string;
  name: string;
  tier: SubscriptionTier;
  price: {
    monthly: number;
    quarterly: number;
    yearly: number;
  };
  features: {
    customAgentLimit: number | 'unlimited';
    baseAgents: string[];
    supportLevel: 'community' | 'standard' | 'priority' | 'dedicated';
    apiAccess: boolean;
    advancedAnalytics: boolean;
    tokenMarkup: number; // 0.10 = 10%
  };
  popular?: boolean;
  description: string;
}

export interface SpecializedProduct {
  id: string;
  productCode: string;
  name: string;
  category: string;
  description: string;
  price: {
    perUserMonthly: number;
    perUserAnnual: number;
  };
  minimumSeats: number;
  maximumSeats?: number;
  includedAgents: string[];
  specializedTools: string[];
  industryCompliance: string[];
  requiresMinimumTier?: SubscriptionTier;
  customAgentLimit?: number | 'unlimited';
  includesUnlimitedAgents: boolean;
}

export interface BillingAccount {
  id: string;
  organizationId: string;
  stripeCustomerId: string;
  billingEmail: string;
  trialEndsAt?: Date;
  isTrialActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Subscription {
  id: string;
  billingAccountId: string;
  stripeSubscriptionId: string;
  planTier: SubscriptionTier;
  billingFrequency: BillingFrequency;
  userCount: number;
  status: SubscriptionStatus;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  canceledAt?: Date;
  customAgentLimit: number | null;
  includesUnlimitedAgents: boolean;
  specializedProducts?: SpecializedProductSubscription[];
  totalMonthlyAmount: number;
  selectedToolkit?: string;
}

export interface SpecializedProductSubscription {
  id: string;
  productId: string;
  product: SpecializedProduct;
  seatCount: number;
  pricePerSeat: number;
  status: string;
  assignedUsers: UserProductAssignment[];
}

export interface UserProductAssignment {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  assignedAt: Date;
  assignedBy?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  lastFour: string;
  brand?: string;
  isDefault: boolean;
}

export interface Invoice {
  id: string;
  stripeInvoiceId: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  dueDate?: Date;
  paidAt?: Date;
  invoicePdf?: string;
  createdAt: Date;
}

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  basePrice: number;
  discountedPrice: number;
  savings: number;
  popular?: boolean;
}

export interface TokenBalance {
  prepurchasedTokens: number;
  usedTokens: number;
  availableTokens: number;
  lastPurchase?: Date;
  lastUsage?: Date;
}

export interface TokenUsage {
  totalTokens: number;
  billableTokens: number;
  prepurchasedTokensUsed: number;
  baseCost: number;
  markupAmount: number;
  totalCost: number;
  period: {
    start: Date;
    end: Date;
  };
}

export interface SetupIntentResponse {
  clientSecret: string;
}

export interface BillingSummary {
  subscription: Subscription;
  currentUsage: {
    activeUsers: number;
    customAgents: number;
    storageGB: number;
    apiCalls: number;
  };
  tokenUsage: TokenUsage;
  tokenBalance: TokenBalance;
  upcomingInvoice?: {
    amount: number;
    dueDate: Date;
  };
}

// Pricing constants
export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    tier: SubscriptionTier.STARTER,
    price: {
      monthly: 29,
      quarterly: 27.55,
      yearly: 26.10
    },
    features: {
      customAgentLimit: 5,
      baseAgents: ['general_assistant', 'research_agent'],
      supportLevel: 'community',
      apiAccess: false,
      advancedAnalytics: false,
      tokenMarkup: 0.10
    },
    description: 'Perfect for individuals and small teams getting started with AI agents'
  },
  {
    id: 'professional',
    name: 'Professional',
    tier: SubscriptionTier.PROFESSIONAL,
    price: {
      monthly: 59,
      quarterly: 56.05,
      yearly: 53.10
    },
    features: {
      customAgentLimit: 10,
      baseAgents: ['general_assistant', 'research_agent', 'analytics_agent', 'writing_agent'],
      supportLevel: 'standard',
      apiAccess: true,
      advancedAnalytics: true,
      tokenMarkup: 0.10
    },
    popular: true,
    description: 'Ideal for growing teams that need more agents and advanced features'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tier: SubscriptionTier.ENTERPRISE,
    price: {
      monthly: 89,
      quarterly: 84.55,
      yearly: 80.10
    },
    features: {
      customAgentLimit: 'unlimited',
      baseAgents: ['general_assistant', 'research_agent', 'analytics_agent', 'writing_agent', 'code_agent', 'data_agent'],
      supportLevel: 'priority',
      apiAccess: true,
      advancedAnalytics: true,
      tokenMarkup: 0.10
    },
    description: 'Unlimited agents and priority support for large organizations'
  },
  {
    id: 'industry_specific',
    name: 'Industry Specific',
    tier: SubscriptionTier.INDUSTRY_SPECIFIC,
    price: {
      monthly: 129,
      quarterly: 122.55,
      yearly: 116.10
    },
    features: {
      customAgentLimit: 'unlimited',
      baseAgents: ['general_assistant', 'research_agent', 'analytics_agent', 'writing_agent', 'code_agent', 'data_agent', 'mep-cad-analyzer', 'mep-hvac-calculator', 'mep-electrical-analyzer', 'mep-coordinator', 'mep-validator'],
      supportLevel: 'priority',
      apiAccess: true,
      advancedAnalytics: true,
      tokenMarkup: 0.10
    },
    description: 'Enterprise features plus your choice of specialized industry toolkit (included in price)'
  }
];

export const TOKEN_PACKAGES: TokenPackage[] = [
  {
    id: 'tokens_1m',
    name: '1 Million Tokens',
    tokens: 1_000_000,
    basePrice: 22,
    discountedPrice: 19.80,
    savings: 2.20
  },
  {
    id: 'tokens_10m',
    name: '10 Million Tokens',
    tokens: 10_000_000,
    basePrice: 220,
    discountedPrice: 198,
    savings: 22,
    popular: true
  },
  {
    id: 'tokens_100m',
    name: '100 Million Tokens',
    tokens: 100_000_000,
    basePrice: 2200,
    discountedPrice: 1980,
    savings: 220
  }
];

export const MEP_SPECIALIZED_PRODUCT: SpecializedProduct = {
  id: 'mep_toolkit',
  productCode: 'MEP_TOOLKIT',
  name: 'MEP Toolkit',
  category: 'engineering',
  description: 'Professional Mechanical, Electrical, and Plumbing toolkit with specialized AI agents for HVAC calculations, electrical load analysis, and AutoCAD drawing processing.',
  price: {
    perUserMonthly: 129,
    perUserAnnual: 1393.20
  },
  minimumSeats: 1,
  includedAgents: ['mep-cad-analyzer', 'mep-hvac-calculator', 'mep-electrical-analyzer', 'mep-coordinator', 'mep-validator'],
  specializedTools: ['cad-processing', 'hvac-calculations', 'electrical-analysis', 'wrightsoft-export', 'climate-data'],
  industryCompliance: ['ASHRAE', 'NEC', 'IBC', 'IECC'],
  includesUnlimitedAgents: true
};